import { useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
// framer-motion removed: sidebar text flash fixed by conditional rendering instead of animation
import {
  LayoutDashboard,
  BookOpen,
  Map,
  TrendingUp,
  Award,
  Trophy,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Gift,
  ArrowLeft,
  PlayCircle,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sheet, SheetContent } from '../ui/sheet';

const NAV_ITEMS = [
  { id: 'dashboard',     Icon: LayoutDashboard, key: 'cabin.nav.dashboard',      path: '/programs/kabin-memurlugu/dashboard' },
  { id: 'modules',       Icon: BookOpen,         key: 'cabin.nav.modules',         path: '/programs/kabin-memurlugu/modules' },
  { id: 'video_lessons', Icon: PlayCircle,       key: 'cabin.nav.video_lessons',   path: '/programs/kabin-memurlugu/video-lessons' },
  { id: 'roadmap',       Icon: Map,              key: 'cabin.nav.roadmap',         path: '/programs/kabin-memurlugu/roadmap' },
  { id: 'progress',      Icon: TrendingUp,       key: 'cabin.nav.progress',        path: '/programs/kabin-memurlugu/progress' },
  { id: 'achievements',  Icon: Award,            key: 'cabin.nav.achievements',    path: '/programs/kabin-memurlugu/achievements' },
  { id: 'leaderboard',   Icon: Trophy,           key: 'cabin.nav.leaderboard',     path: '/programs/kabin-memurlugu/leaderboard' },
  { id: 'interview',     Icon: MessageCircle,    key: 'cabin.nav.interview',       path: '/programs/kabin-memurlugu/interview' },
  { id: 'spinWheel',     Icon: Gift,             key: 'cabin.nav.spinWheel',       path: '/programs/kabin-memurlugu/spin-wheel' },
];

function SidebarItem({ item, isCollapsed, t }) {
  return (
    <li role="none">
      <NavLink
        to={item.path}
        role="menuitem"
        aria-label={t(item.key)}
        data-testid={`cabin-nav-${item.id}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
            isActive
              ? 'border-l-[3px] border-[var(--brand-primary)] bg-sky-50 text-[var(--brand-primary)] font-semibold pl-[calc(0.75rem-3px)]'
              : 'text-gray-600 hover:bg-gray-100 border-l-[3px] border-transparent'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <item.Icon
              size={20}
              strokeWidth={2.5}
              aria-hidden="true"
              className={`shrink-0 ${isActive ? 'text-[var(--brand-primary)]' : 'text-gray-500'}`}
            />
            {!isCollapsed && (
              <span className="whitespace-nowrap overflow-hidden text-sm">
                {t(item.key)}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

function SidebarContent({ isCollapsed, onToggle, t, user, onLogout }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo + toggle */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        {!isCollapsed && (
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-xl" aria-hidden="true">✈️</span>
            <span className="text-sm font-black text-[var(--text-main)] whitespace-nowrap">
              AirAcademia
            </span>
          </div>
        )}
        {isCollapsed && (
          <span className="text-xl mx-auto" aria-hidden="true">✈️</span>
        )}
        <button
          onClick={onToggle}
          aria-expanded={!isCollapsed}
          aria-controls="cabin-sidebar-nav"
          aria-label={t('cabin.nav.toggle_label')}
          data-testid="cabin-sidebar-toggle"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ml-auto shrink-0"
        >
          {isCollapsed
            ? <ChevronRight size={18} aria-hidden="true" />
            : <ChevronLeft size={18} aria-hidden="true" />
          }
        </button>
      </div>

      {/* Nav items */}
      <nav
        aria-label={t('cabin.nav.aria_label')}
        role="navigation"
        className="flex-1 overflow-y-auto py-3 px-2"
      >
        <ul id="cabin-sidebar-nav" role="menubar" className="flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              isCollapsed={isCollapsed}
              t={t}
            />
          ))}
        </ul>
      </nav>

      {/* Back to Academy Pro */}
      <div className="px-2 pb-1">
        <NavLink
          to="/dashboard"
          aria-label={t('cabin.nav.back_to_academy')}
          data-testid="cabin-nav-back-to-academy"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150
                     text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 border-2 border-transparent
                     hover:border-emerald-200 focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-emerald-500 group"
        >
          <ArrowLeft
            size={20}
            strokeWidth={2.5}
            aria-hidden="true"
            className="shrink-0 text-gray-400 group-hover:text-emerald-600 transition-colors"
          />
          {!isCollapsed && (
            <span className="whitespace-nowrap overflow-hidden text-sm font-semibold">
              {t('cabin.nav.back_to_academy')}
            </span>
          )}
        </NavLink>
      </div>

      {/* Footer: user mini-card + logout */}
      <div className="border-t border-gray-100 p-3 flex flex-col gap-2">
        {/* User row */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-indigo-500 flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-xs font-semibold text-[var(--text-main)] truncate">
                {user?.name || user?.email || 'User'}
              </p>
            </div>
          )}
          <button
            onClick={onLogout}
            title={t('logout') || 'Logout'}
            aria-label={t('logout') || 'Logout'}
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 shrink-0 ml-auto"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CabinSidebar — collapsed 64px / expanded 256px desktop
 *                + Sheet drawer for mobile (<768px)
 */
export default function CabinSidebar({
  isCollapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
}) {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const liveRef = useRef(null);

  const handleLogout = () => {
    logout?.();
    navigate('/login');
  };

  // Alt+S keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key === 's') {
        e.preventDefault();
        onToggle();
        if (liveRef.current) {
          liveRef.current.textContent = isCollapsed
            ? t('cabin.a11y.sidebar_expanded')
            : t('cabin.a11y.sidebar_collapsed');
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isCollapsed, onToggle, t]);

  return (
    <>
      {/* ARIA live region for sidebar toggle announcement */}
      <div
        ref={liveRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col bg-white border-r-2 border-gray-200 fixed top-0 left-0 h-full z-30 transition-all duration-200 ease-out ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.04)' }}
      >
        <SidebarContent
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          t={t}
          user={user}
          onLogout={handleLogout}
        />
      </aside>

      {/* Mobile Sheet drawer */}
      <Sheet open={mobileOpen} onOpenChange={(v) => !v && onMobileClose()}>
        <SheetContent side="left" className="p-0 w-72 md:hidden" aria-label={t('cabin.nav.aria_label')}>
          <SidebarContent
            isCollapsed={false}
            onToggle={onMobileClose}
            t={t}
            user={user}
            onLogout={handleLogout}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
